import React, { useEffect, useState } from 'react'
import { SelectionJoin } from 'components/sidebar/surface'

export interface SelectionState {
  selected: string[]
  anchor: string | null
}

export const EMPTY_SELECTION: SelectionState = { selected: [], anchor: null }

export type SelectionClick = { id: string; toggle: boolean; range: boolean }

/**
 * Finder rules: ⌘/Ctrl toggles, Shift selects the visible range from the anchor, a plain click clears.
 *
 * A selection holds rows of one folder, or of the top level: what `parentOf` says a row sits in. A
 * ⌘- or Shift-click on a row somewhere else starts a new selection from it, and a Shift range takes
 * only the rows at the anchor's level, skipping what's open inside them.
 *
 * `openId` is the row whose page is open. Its row is highlighted, so it looks selected: a selection
 * started with nothing selected starts from it (⌘-click adds to it, Shift-click with no anchor ranges
 * from it), as the clicked item in Finder is, when it's at the same level. ⌘-clicking it takes it out
 * like any other row.
 */
export const nextSelection = (
  state: SelectionState,
  click: SelectionClick,
  visibleOrder: string[],
  openId: string | null = null,
  parentOf: (id: string) => string | null = () => null
): SelectionState => {
  const level = parentOf(click.id)
  const atLevel = (id: string): boolean => parentOf(id) === level
  const open =
    state.selected.length === 0 && openId !== null && visibleOrder.includes(openId) && atLevel(openId) ? openId : null
  const current = state.selected.length > 0 && atLevel(state.selected[0]) ? state.selected : []
  const anchor = [state.anchor, open].find((id): id is string => id !== null && atLevel(id)) ?? null
  if (click.range && anchor) {
    const from = visibleOrder.indexOf(anchor)
    const to = visibleOrder.indexOf(click.id)
    if (from !== -1 && to !== -1) {
      const [start, end] = from < to ? [from, to] : [to, from]
      return { selected: visibleOrder.slice(start, end + 1).filter(atLevel), anchor }
    }
  }
  if (click.toggle || click.range) {
    const base = open && open !== click.id ? [open] : current
    const selected = base.includes(click.id) ? base.filter((id) => id !== click.id) : [...base, click.id]
    return { selected, anchor: click.id }
  }
  return { selected: [], anchor: click.id }
}

/**
 * Which of a marked row's neighbours in `order` are marked too, so a run of marked rows draws as one
 * block: each squares the corners it shares with them.
 */
export const joinOf = (order: string[], id: string, marked: (id: string) => boolean): SelectionJoin => {
  if (!marked(id)) return 'none'
  const at = order.indexOf(id)
  const above = at > 0 && marked(order[at - 1])
  const below = at !== -1 && at < order.length - 1 && marked(order[at + 1])
  if (above && below) return 'both'
  if (above) return 'above'
  return below ? 'below' : 'none'
}

/** The modifier keys of a click, or of the Enter/Space press that activates a row from the keyboard. */
export type SelectionModifiers = Pick<React.MouseEvent, 'metaKey' | 'ctrlKey' | 'shiftKey'>

export interface SidebarSelection {
  /** Visible row ids, in the order they were selected. */
  selected: string[]
  /**
   * Applies a row click to the selection. True when the click was a selection gesture: the caller
   * then calls `preventDefault()`, so ⌘-click selects instead of opening a tab. ⌘/Ctrl-click always
   * is, on every row; Shift-click is on a row that may be selected.
   */
  handleRowClick: (event: SelectionModifiers, id: string) => boolean
  clear: () => void
  isSelected: (id: string) => boolean
}

/**
 * Multi-select for the sidebar tree. With `enabled` false (viewers who can't move anything) it
 * never selects anything.
 *
 * `visible` is the rows on screen that may be selected, top to bottom. Shift ranges over it. A row
 * that leaves it (collapsed away, deleted, hidden by a refresh, or no longer movable) leaves the
 * selection for good, so a drag never carries rows the user can't see or move.
 *
 * ⌘/Ctrl-click toggles a row in or out, as in Finder, on every row: one that can't be selected just
 * isn't added, and the click doesn't open the row either (no new tab, no opening a folder). A new
 * tab is a middle-click, or Open in new tab in the row's menu. Shift-click on a row that can't be
 * selected is left alone, and a plain click on any row clears the selection. So does a click
 * anywhere outside the tree, as in Finder, unless `held`: a menu or dialog that acts on the
 * selection is open, and a click elsewhere only closes it.
 *
 * `openId` is the row whose page is open, which a selection starts from, and `parentOf` the folder
 * a row sits in, which keeps a selection to one level (see nextSelection).
 */
export const useSidebarSelection = (
  enabled: boolean,
  visible: string[],
  openId: string | null = null,
  held = false,
  parentOf: (id: string) => string | null = () => null
): SidebarSelection => {
  const [state, setState] = useState<SelectionState>(EMPTY_SELECTION)
  const visibleSet = new Set(visible)
  const selected = enabled ? state.selected.filter((id) => visibleSet.has(id)) : EMPTY_SELECTION.selected
  const selectedSet = new Set(selected)
  const hasSelection = selected.length > 0

  // Hidden rows leave the state too, so they don't come back when their folder reopens. Adjusted
  // during render rather than in an effect, so every hide prunes, however many come in a row.
  if (enabled && selected.length < state.selected.length) setState({ ...state, selected })

  useEffect(() => {
    if (!hasSelection) return
    const onKeyDown = (event: KeyboardEvent): void => {
      // An Escape that already closed something else (a rename box, a menu) isn't also a deselect.
      if (event.key === 'Escape' && !event.defaultPrevented) setState(EMPTY_SELECTION)
    }
    // Clicks on the tree are the rows' own (and its empty space clears it there).
    const onPointerDown = (event: PointerEvent): void => {
      if (held || event.button !== 0) return
      if (event.target instanceof Element && event.target.closest('[data-sidebar-tree]')) return
      setState(EMPTY_SELECTION)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [hasSelection, held])

  const handleRowClick: SidebarSelection['handleRowClick'] = (event, id) => {
    const click = { id, toggle: event.metaKey || event.ctrlKey, range: event.shiftKey }
    if (!enabled || !visibleSet.has(id)) {
      if (!click.toggle && !click.range) setState(EMPTY_SELECTION)
      return click.toggle
    }
    setState((prev) => nextSelection(prev, click, visible, openId, parentOf))
    return click.toggle || click.range
  }

  return {
    selected,
    handleRowClick,
    clear: () => setState(EMPTY_SELECTION),
    isSelected: (id) => selectedSet.has(id)
  }
}
